import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center whitespace-nowrap font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        brand:
          "bg-background-brand-bold text-content-primary-inverse hover:opacity-90 active:opacity-80 focus-visible:ring-border-focus disabled:opacity-40",
        brandSubtle:
          "bg-background-brand-subtle text-content-brand-bold hover:bg-background-brand-subtle/80 active:bg-background-brand-subtle/60 focus-visible:ring-border-focus disabled:opacity-40",
        primary:
          "bg-background-primary-bold text-content-primary-inverse hover:bg-background-primary-bold-hover active:bg-background-primary-bold-hover focus-visible:ring-border-focus disabled:bg-surface-L2 disabled:text-content-primary-subtle",
        secondary:
          "bg-surface-L2 text-content-secondary-bold hover:bg-surface-L2/75 active:bg-surface-L2/60 focus-visible:ring-border-focus disabled:bg-surface-L2 disabled:text-content-primary-subtle disabled:opacity-50",
        outline:
          "border-[1.5px] border-border-brand-bold bg-surface-L1 text-content-brand-bold hover:bg-background-brand-subtle active:bg-background-brand-subtle/80 focus-visible:ring-border-focus disabled:border-border-L3 disabled:text-content-primary-subtle disabled:opacity-50",
        destructive:
          "border-[1.5px] border-content-negative-bold bg-transparent text-content-negative-bold hover:bg-background-negative-subtle active:bg-background-negative-subtle/80 focus-visible:ring-content-negative-bold disabled:border-border-L3 disabled:text-content-primary-subtle disabled:opacity-50",
        negative:
          "bg-background-negative-subtle text-content-negative-bold hover:border-[1.5px] hover:border-content-negative-bold focus-visible:ring-content-negative-bold disabled:opacity-50",
        positive:
          "bg-background-positive-subtle text-content-positive-bold hover:border-[1.5px] hover:border-content-positive-default focus-visible:ring-content-positive-default disabled:opacity-50",
      },
      size: {
        mini: "h-8 px-3 py-1.5 gap-1.5 text-xs rounded-md [&_svg]:size-3",
        sm: "h-8 px-3 py-1.5 gap-2 text-xs rounded-md [&_svg]:size-4",
        default: "h-11 px-6 py-2.5 gap-2 text-sm rounded-lg [&_svg]:size-4",
        lg: "h-12 px-8 py-3 gap-2 text-base rounded-lg [&_svg]:size-5",
        icon: "h-9 w-9 p-0 [&_svg]:size-4",
      },
      roundness: {
        default: "",
        round: "rounded-full",
      },
    },
    compoundVariants: [
      { roundness: "round", size: "mini", className: "rounded-full" },
      { roundness: "round", size: "sm", className: "rounded-full" },
      { roundness: "round", size: "default", className: "rounded-full" },
      { roundness: "round", size: "lg", className: "rounded-full" },
      { roundness: "round", size: "icon", className: "rounded-full" },
    ],
    defaultVariants: {
      variant: "primary",
      size: "default",
      roundness: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      roundness,
      asChild = false,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, roundness, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Loader2 className="animate-spin" /> : children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
